import { Trans } from "react-i18next";import { useEffect, useState } from "react";

import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle } from
"../../../components/ui/dialog";
import { getDetailedReport } from "../../../services/attendanceService";

import { formatDate, formatDuration } from "../utils/dateUtils";

function Row({ label, value }) {

  const isLongText = typeof value === "string" && (value.includes("\n") || value.length > 30);

  if (isLongText) {
    return (
      <div className="py-2.5">
        <span className="text-sm font-semibold per-file-hint">{label}</span>
        <p className="text-sm text-foreground mt-1 whitespace-pre-line leading-relaxed wrap-break-word">{value}</p>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="text-sm font-semibold per-file-hint shrink-0">{label}</span>
      <span className="text-sm text-right text-foreground min-w-0 wrap-break-word">{value ?? <span className="per-file-hint">—</span>}</span>
    </div>);

}



// Builds "trips" out of the flat chronological Entry/Exit list:
// - An Exit immediately followed by an Entry is a consecutive pair -> one
//   trip block showing the Exit's location/time/activities (reason) plus
//   the Entry's return time/location.
// - An Exit NOT immediately followed by an Entry (e.g. the last exit before
//   final checkout) stands alone -> trip block with just the Exit's
//   location/time/activities, no entry side.
// - A stray Entry not preceded by an Exit (shouldn't normally happen) is
//   skipped since it has no exit to pair with here.
function buildTrips(events) {
  const trips = [];
  let i = 0;
  while (i < events.length) {
    const current = events[i];
    if (current.intermediateAction === "Exit") {
      const next = events[i + 1];
      if (next && next.intermediateAction === "Entry") {
        trips.push({ exit: current, entry: next });
        i += 2;
      } else {
        trips.push({ exit: current, entry: null });
        i += 1;
      }
    } else {
      i += 1;
    }
  }
  return trips;
}

// Report view for the "View Report" mobile action: shows only the
// intermediate (mid-day) exit/entry trips for the day, pairing consecutive
// Exit->Entry events together. "Activities Completed" is sourced from the
// intermediate Exit's `reason` field (NOT record.activitiesCompleted, which
// belongs to the final checkout). No check-in/check-out rows are shown here.
export function ViewReportModal({ record, onOpenChange }) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!record?.attendanceId) {
      setTrips([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    getDetailedReport(record.attendanceId)
    .then((data) => {
      const events = Array.isArray(data) ? data : [];
      const filtered = events
      .filter((e) => e.intermediateAction === "Entry" || e.intermediateAction === "Exit")
      .sort((a, b) => new Date(a.actionTimestamp) - new Date(b.actionTimestamp));
      setTrips(buildTrips(filtered));
    })
    .catch((err) => setError(err.message))
    .finally(() => setLoading(false));
  }, [record]);

  return (
    <Dialog open={!!record} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border border-white/60 bg-white shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] font-sans">
          {record &&
          <>
              <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-text-border)/70">
                <div>
                  <DialogTitle className="text-xl font-bold text-foreground"><Trans i18nKey="attendance.viewReport" /></DialogTitle>
                  <p className="text-sm per-file-hint mt-0.5">
                    {record.empName || "—"} <span className="text-(--color-text-border)">·</span> <Trans i18nKey="common.id" /> {record.empId ?? "—"}
                  </p>
                  <p className="text-sm per-file-hint">{formatDate(record.attendanceDate)}</p>
                </div>
                <DialogPrimitive.Close className="h-9 w-9 grid place-items-center rounded-xl per-file-hint hover:bg-workspace/60 hover:text-primary transition cursor-pointer">
                  <X className="h-5 w-5" />
                  <span className="sr-only"><Trans i18nKey="formActions.close" /></span>
                </DialogPrimitive.Close>
              </div>

              <div className="px-6 py-2">
                {loading && <p className="py-3 text-sm per-file-hint"><Trans i18nKey="attendance.loadingIntermediateActivity" /></p>}
                {error && <p className="py-3 text-sm text-error">{error}</p>}

                {!loading && !error && trips.length === 0 &&
              <p className="py-6 text-sm text-center per-file-hint"><Trans i18nKey="No Intermediate Activity" /></p>
              }

                {!loading && trips.length > 0 &&
              <div className="divide-y divide-(--color-text-border)/40">
                    {trips.map((trip, i) =>
                <div key={i} className="py-3 space-y-1">
                        <Row label="Location" value={trip.exit.location} />
                        <Row label="Work Duration" value={formatDuration(trip.exit.totalWorkMinutes)} />
                        {trip.exit.reason ? <Row label="Activities Completed" value={trip.exit.reason} /> : null}
                      </div>
                )}
                  </div>

              }
                <div className="py-2.5">
                  <div className="text-sm font-semibold per-file-hint">
                    Check out - Activities Completed
                  </div>
                  <div className="text-sm text-foreground mt-1 wrap-break-word">
                    {record.activitiesCompleted}
                  </div>
                </div>
              </div>

              <div className="h-4" />
            </>
          }
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>);

}